import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '../../../lib/supabase';
import phaseBillingService from '../../../services/phaseBillingService';
import { resolvePrimaryWorkshopRole } from '../../../lib/workshopOperatorView';
import {
  WORKSHOP_ATELIER_ROLE_TYPES,
  PRODUCTION_MANAGER_ROLE_TYPES,
} from '../../../lib/operatorRoles';
import { userSeesAllProductionLots } from '../../../lib/productionLotVisibility';
import { filterBatchesForTrackingDashboard } from '../../../lib/productionTrackingQueues';
import {
  applyProductionFilters,
  DEFAULT_PRODUCTION_FILTERS,
  isWorkshopWideSearchActive,
  normalizeBatch,
  normalizeMachine,
  normalizeOperator,
} from '../../../lib/productionTrackingUtils';

export function useProductionTrackingData() {
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => {
    const caseParam = searchParams.get('case');
    return {
      ...DEFAULT_PRODUCTION_FILTERS,
      ...(caseParam ? { case: caseParam } : {}),
    };
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [rawBatches, setRawBatches] = useState([]);
  const [machines, setMachines] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userRoleTypes, setUserRoleTypes] = useState([]);

  const visibilityCtx = useMemo(
    () => ({
      userId: currentUser?.id,
      primaryRole: userRole,
      roleTypes: userRoleTypes,
    }),
    [currentUser?.id, userRole, userRoleTypes]
  );

  const isManagerView = useMemo(
    () => userSeesAllProductionLots(userRole, userRoleTypes),
    [userRole, userRoleTypes]
  );

  const workshopWideSearch = useMemo(
    () => isWorkshopWideSearchActive(filters, searchQuery),
    [filters, searchQuery]
  );

  const stationBatches = useMemo(
    () => filterBatchesForTrackingDashboard(rawBatches, visibilityCtx),
    [rawBatches, visibilityCtx]
  );

  const productionBatches = useMemo(
    () =>
      workshopWideSearch
        ? filterBatchesForTrackingDashboard(rawBatches, visibilityCtx, {
            includeWholeWorkshop: true,
          })
        : stationBatches,
    [rawBatches, visibilityCtx, workshopWideSearch, stationBatches]
  );

  const filteredBatches = useMemo(
    () => applyProductionFilters(productionBatches, filters, searchQuery),
    [productionBatches, filters, searchQuery]
  );

  const canOpenWorkshop = useMemo(() => {
    const roles = userRoleTypes?.length ? userRoleTypes : userRole ? [userRole] : [];
    if (roles.includes('administrator')) return true;
    if (PRODUCTION_MANAGER_ROLE_TYPES.some((r) => roles.includes(r))) return true;
    return WORKSHOP_ATELIER_ROLE_TYPES.some((r) => roles.includes(r));
  }, [userRole, userRoleTypes]);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, full_name, email')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      const { data: roleAssignments, error: roleError } = await supabase
        .from('user_role_assignments')
        .select(`
          role_id,
          is_active,
          user_roles ( role_type, role_name )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (roleError) console.error('Error fetching roles:', roleError);

      const roleTypes = (roleAssignments || [])
        .map((ra) => ra.user_roles?.role_type)
        .filter(Boolean);

      setCurrentUser(profile);
      setUserRoleTypes(roleTypes);
      setUserRole(resolvePrimaryWorkshopRole(roleTypes) || roleTypes[0] || null);
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  }, []);

  const fetchProductionBatches = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from('production_batches')
      .select(`
        *,
        machine:machines ( id, machine_name, machine_code, status ),
        case:cases (
          id,
          case_number,
          dentist:user_profiles!cases_user_profile_id_fkey(full_name),
          case_timeline_phases (
            id, phase_name, status, responsible_user_id, phase_order, group
          )
        ),
        responsible_user:user_profiles!production_batches_responsible_fkey ( id, full_name )
      `)
      .order('deadline', { ascending: true });

    if (fetchError) throw fetchError;
    setRawBatches((data || []).map(normalizeBatch));
  }, []);

  const fetchMachines = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from('machines')
      .select(`
        *,
        current_batch:production_batches ( batch_number, progress, status )
      `)
      .order('machine_name', { ascending: true });

    if (fetchError) throw fetchError;

    const withBatch = (data || []).map((m) => ({
      ...m,
      current_batch:
        (m.current_batch || []).find((b) => b.status === 'in_progress') || null,
    }));

    setMachines(withBatch.map(normalizeMachine));
  }, []);

  const fetchPersonnel = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from('operators')
      .select('*')
      .eq('is_active', true)
      .order('full_name', { ascending: true });

    if (fetchError) throw fetchError;
    setPersonnel((data || []).map(normalizeOperator));
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchProductionBatches(),
        fetchMachines(),
        fetchPersonnel(),
      ]);
    } catch (err) {
      console.error(err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [fetchProductionBatches, fetchMachines, fetchPersonnel]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    if (currentUser) refreshAll();
  }, [currentUser, refreshAll]);

  useEffect(() => {
    const caseParam = searchParams.get('case');
    if (caseParam) {
      setFilters((prev) => ({ ...prev, case: caseParam }));
    }
  }, [searchParams]);

  const handleUpdateBatch = useCallback(
    async (id, batchData) => {
      const now = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('production_batches')
        .update({ ...batchData, updated_at: now })
        .eq('id', id);
      if (updateError) throw updateError;
      await fetchProductionBatches();
      if ('model_up_from' in batchData || 'packaging_date' in batchData) {
        toast.success('Fiche de production mise à jour');
      }
    },
    [fetchProductionBatches]
  );

  const handleDeleteBatch = useCallback(
    async (id) => {
      if (!window.confirm('Supprimer ce lot ?')) return;
      const { error: deleteError } = await supabase
        .from('production_batches')
        .delete()
        .eq('id', id);
      if (deleteError) throw deleteError;
      await fetchProductionBatches();
    },
    [fetchProductionBatches]
  );

  const handleUpdateStatus = useCallback(
    async (id, status) => {
      const { error: updateError } = await supabase
        .from('production_batches')
        .update({ status })
        .eq('id', id);
      if (updateError) throw updateError;
      await fetchProductionBatches();
    },
    [fetchProductionBatches]
  );

  const handleAssignMachine = useCallback(
    async (batchId, machineId) => {
      const { error: updateError } = await supabase
        .from('production_batches')
        .update({ machine_id: machineId })
        .eq('id', batchId);
      if (updateError) throw updateError;
      await Promise.all([fetchProductionBatches(), fetchMachines()]);
    },
    [fetchProductionBatches, fetchMachines]
  );

  const handleCreateProductionSheet = useCallback(
    async (batchId, sheetData) => {
      const { error: updateError } = await supabase
        .from('production_batches')
        .update({
          ...sheetData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', batchId);
      if (updateError) throw updateError;
      await fetchProductionBatches();
      toast.success('Fiche de production enregistrée');
    },
    [fetchProductionBatches]
  );

  const handleLaunchProduction = useCallback(
    async (batchId) => {
      const now = new Date().toISOString();
      const { error: batchError } = await supabase
        .from('production_batches')
        .update({ status: 'in_progress', start_date: now, progress: 5 })
        .eq('id', batchId);
      if (batchError) throw batchError;

      const { data: batch, error: fetchError } = await supabase
        .from('production_batches')
        .select('case_id')
        .eq('id', batchId)
        .single();
      if (fetchError) throw fetchError;

      if (batch?.case_id) {
        const { data: phase, error: phaseError } = await supabase
          .from('case_timeline_phases')
          .select('id')
          .eq('case_id', batch.case_id)
          .eq('group', '1')
          .eq('phase_order', 4)
          .single();
        if (!phaseError && phase?.id) {
          const { data: milestone, error: msError } = await supabase
            .from('phase_milestones')
            .select('id')
            .eq('phase_id', phase.id)
            .eq('milestone_order', 1)
            .single();
          if (!msError && milestone?.id) {
            await supabase
              .from('phase_milestones')
              .update({
                is_completed: true,
                completed_date: now,
                updated_at: now,
              })
              .eq('id', milestone.id);
          }
        }
      }
      await fetchProductionBatches();
    },
    [fetchProductionBatches]
  );

  const handleAssignResponsible = useCallback(
    async (batchId, userId) => {
      const now = new Date().toISOString();
      const { error: batchError } = await supabase
        .from('production_batches')
        .update({ responsible: userId, updated_at: now })
        .eq('id', batchId);
      if (batchError) throw batchError;

      const { data: batch, error: fetchError } = await supabase
        .from('production_batches')
        .select('case_id')
        .eq('id', batchId)
        .single();
      if (!fetchError && batch?.case_id) {
        await supabase
          .from('cases')
          .update({ responsible: userId, updated_at: now })
          .eq('id', batch.case_id);
        await supabase
          .from('case_timeline_phases')
          .update({ responsible_user_id: userId, updated_at: now })
          .eq('case_id', batch.case_id)
          .eq('group', '1')
          .eq('phase_order', 4);
      }
      await fetchProductionBatches();
      toast.success('Responsable assigné');
    },
    [fetchProductionBatches]
  );

  const handleGenerateInvoiceForBatch = useCallback(
    async (batch) => {
      toast.info('Vérification facturation…', { duration: 2000 });
      const billingResult = await phaseBillingService.checkAndTriggerBillingForBatch(
        batch.id
      );
      if (!billingResult.success) {
        toast.error(billingResult.message);
        return;
      }
      if (
        billingResult.shouldGenerateInvoice &&
        billingResult.invoicesGenerated?.length > 0
      ) {
        const invoiceNumbers = billingResult.invoicesGenerated
          .map((i) => i.invoice.invoice_number)
          .join(', ');
        toast.success('Facture(s) générée(s)', {
          description: `${invoiceNumbers} — ${billingResult.totalAligners} aligneurs`,
        });
      } else {
        toast.info(billingResult.message);
      }
      await fetchProductionBatches();
    },
    [fetchProductionBatches]
  );

  const handleResetFiltersAndSearch = useCallback(() => {
    setFilters({ ...DEFAULT_PRODUCTION_FILTERS });
    setSearchQuery('');
  }, []);

  const handleExport = useCallback(() => {
    const headers = [
      'N° lot',
      'N° conception',
      'Dentiste',
      'Patient',
      'Clinique',
      'Statut',
      'Priorité',
      'Progression %',
      'Début',
      'Livraison',
    ];
    const rows = filteredBatches.map((b) => [
      b.batchNumber,
      b.caseNumber || '',
      b.dentistName || '',
      b.patientName,
      b.clinicName,
      b.status,
      b.priority,
      b.progress,
      b.startDate ? new Date(b.startDate).toLocaleDateString('fr-FR') : '',
      b.deadline ? new Date(b.deadline).toLocaleDateString('fr-FR') : '',
    ]);
    const csv = [headers, ...rows]
      .map((r) =>
        r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')
      )
      .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(
      new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    );
    a.download = `production_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  }, [filteredBatches]);

  const batchHandlers = useMemo(
    () => ({
      onUpdate: handleUpdateBatch,
      onDelete: handleDeleteBatch,
      onStatusChange: handleUpdateStatus,
      onAssignMachine: handleAssignMachine,
      onCreateSheet: handleCreateProductionSheet,
      onLaunchProduction: handleLaunchProduction,
      onAssignResponsible: handleAssignResponsible,
      onGenerateInvoice: handleGenerateInvoiceForBatch,
    }),
    [
      handleUpdateBatch,
      handleDeleteBatch,
      handleUpdateStatus,
      handleAssignMachine,
      handleCreateProductionSheet,
      handleLaunchProduction,
      handleAssignResponsible,
      handleGenerateInvoiceForBatch,
    ]
  );

  return {
    filters,
    setFilters,
    searchQuery,
    setSearchQuery,
    productionBatches,
    filteredBatches,
    readyBatchCount: stationBatches.length,
    workshopWideSearch,
    machines,
    personnel,
    loading,
    error,
    currentUser,
    userRole,
    userRoleTypes,
    visibilityCtx,
    isManagerView,
    canOpenWorkshop,
    refreshAll,
    handleResetFiltersAndSearch,
    handleExport,
    batchHandlers,
  };
}
